import { NgClass } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    signal,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { ToastAction, ToastConfig } from './types';

@Component({
  selector: 'tx-toast',
  templateUrl: './tx-toast.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [MatIconModule, NgClass],
})
export class TxToastComponent implements OnInit, OnDestroy {
  @Input({ required: true }) toast!: ToastConfig;
  @Output() dismissed = new EventEmitter<void>();

  /** 0–100, drives the progress bar shrink */
  readonly progress = signal(100);

  // Color config — all class strings must be literals for Tailwind to scan them
  private readonly _typeConfig: Record<string, { border: string; iconBg: string; bar: string; action: string; labelColor: string }> = {
    request:  { border: 'border-l-orange-500', iconBg: 'bg-orange-500', bar: 'bg-orange-400', action: 'text-orange-600 hover:bg-orange-50', labelColor: 'text-orange-500' },
    client:   { border: 'border-l-blue-500',   iconBg: 'bg-blue-500',   bar: 'bg-blue-400',  action: 'text-blue-600 hover:bg-blue-50',   labelColor: 'text-blue-500'   },
    reminder: { border: 'border-l-green-500',  iconBg: 'bg-green-500',  bar: 'bg-green-400', action: 'text-green-600 hover:bg-green-50',  labelColor: 'text-green-500'  },
    default:  { border: 'border-l-orange-500', iconBg: 'bg-orange-500', bar: 'bg-orange-400', action: 'text-orange-600 hover:bg-orange-50', labelColor: 'text-orange-500' },
  };

  get colors() {
    const t = (this.toast.type ?? 'default') as string;
    return this._typeConfig[t] ?? this._typeConfig['default'];
  }

  actionClass(action: ToastAction): string {
    if (action.style === 'neutral') return 'text-slate-500 hover:bg-slate-50';
    if (action.style === 'danger')  return 'text-red-500 hover:bg-red-50';
    return this.colors.action;
  }

  private _timer: ReturnType<typeof setTimeout> | null = null;
  private _interval: ReturnType<typeof setInterval> | null = null;

  ngOnInit(): void {
    const duration = this.toast.duration ?? 12000;
    if (duration === 0) return;

    const step = 50; // ms tick
    let elapsed = 0;

    this._interval = setInterval(() => {
      elapsed += step;
      this.progress.set(Math.max(0, 100 - (elapsed / duration) * 100));
    }, step);

    this._timer = setTimeout(() => this.dismiss(), duration);
  }

  ngOnDestroy(): void {
    if (this._timer) clearTimeout(this._timer);
    if (this._interval) clearInterval(this._interval);
  }

  dismiss(): void {
    this.dismissed.emit();
  }

  onAction(cb: () => void): void {
    cb();
    this.dismiss();
  }
}
